import { useState } from "react";
import {
	calculateSlotWidth,
	formatTooltipDateRange,
	getDateList,
	isShiftWithinAbsentDate,
	shiftListWithMissingHours,
} from "@utils/dateUtils";
import type {
	Consultant,
	ConsultantAbsence,
	JobApplication,
	JobStatusValues,
	ScheduleShiftItem,
	ScheduleTimeReport,
	ShiftItemWithMissingHours,
	ShiftStatusValues,
	ToolTipData,
} from "@models/Schedule";
import {
	Box,
	Button,
	HStack,
	Popover,
	PopoverArrow,
	PopoverBody,
	PopoverCloseButton,
	PopoverContent,
	PopoverHeader,
	PopoverTrigger,
	Portal,
	Tooltip,
	useDisclosure,
} from "@chakra-ui/react";
import moment from "moment";
import ShiftToolTip from "./ShiftToolTip";

interface Props {
	startDate: Date; //*
	endDate: Date; //*
	isJobView: boolean; //*
	shiftItems: ScheduleShiftItem[]; //*
	jobStatus?: JobStatusValues; // jobView
	jobApplications?: JobApplication[]; // jobView
	timeReports?: ScheduleTimeReport[]; // consultantView
	absences?: ConsultantAbsence[]; // consultantView
	competenceLabelByKey?: Map<string, string>; // consultantView
}

const ShiftViewList = ({
	isJobView,
	startDate,
	endDate,
	shiftItems,
	jobStatus,
	jobApplications,
	timeReports,
	absences,
	competenceLabelByKey,
}: Props) => {
	const shiftData = getDateList(startDate, endDate, shiftItems);

	const getConsultantForShift = (shift?: ScheduleShiftItem) => {
		return (
			shift && shift.id && jobApplications?.find((ja) => !!ja.shifts?.find((item) => item.id === shift?.id))?.consultant
		);
	};

	const getFillColor = (shift?: ScheduleShiftItem): ToolTipData => {
		if (isJobView) {
			const consultantForShift = getConsultantForShift(shift) as Consultant;
			// EXTEND_REQUESTED stays on the shift even after a consultant accepts; treat the shift as
			// booked once an application links a consultant to it.
			if (shift?.status && !consultantForShift) {
				if (shift.status === "EXTEND_REQUESTED" as ShiftStatusValues)
					return { color: "#FFA500", description: "Inväntar svar på förfrågan om att förlänga uppdrag." };
				if (shift.status === "EXTEND_DECLINED" as ShiftStatusValues)
					return { color: "#C44747", description: "Passet har nekats." };
			}
			if (consultantForShift && consultantForShift?.absences && shift?.start && shift.end) {
				if (isShiftWithinAbsentDate(shift, consultantForShift?.absences))
					return { color: "#219CD0", description: "Konsult registrerad som frånvarande." };
			}
			if (shift?.start && shift.end && jobStatus) {
				if (consultantForShift) return { color: "#4BB769", description: "Uppdraget är bokat." };
				if (jobStatus === "OPEN") return { color: "#BDBDBD", description: "Konsult ej tillsatt ännu." };
				if (jobStatus === "BOOKED") return { color: "#4BB769", description: "Uppdraget är bokat." };
			}
			// TODO: need to double check if there is such a scenario
			if (shift?.id) return { color: "#858484", description: "" };
		} else {
			// all the function calls for get color and description for consultantView
			if (shift?.status) {
				if (shift.status === "EXTEND_REQUESTED" as ShiftStatusValues)
					return { color: "#FFA500", description: "Inväntar svar på förfrågan om att förlänga uppdrag." };
				if (shift.status === "EXTEND_DECLINED" as ShiftStatusValues)
					return { color: "#C44747", description: "Passet har nekats." };
			}
			if (shift && isShiftWithinAbsentDate(shift, absences))
				return { color: "#219CD0", description: "Konsult registrerad som frånvarande." };
			if (shift?.start && shift?.end) {
				// since all the shifts are booked status in consultant view
				return { color: "#4BB769", description: "Uppdraget är bokat." };
			}
			// TODO: need to double check if there is such a scenario
			if (shift?.id) return { color: "#858484", description: "" };
		}
		return { color: "", description: "" };
	};

	const getJobDataForConsultant = (shift?: ScheduleShiftItem): { label: string; url: string } | undefined => {
		if (!shift) return undefined;
		const selectedTimeReportJob = timeReports?.find((tr) => tr.shift.id === shift.id)?.job;
		if (!!selectedTimeReportJob && competenceLabelByKey)
			return {
				label: `#${selectedTimeReportJob.orderNumber}: ${competenceLabelByKey.get(
					selectedTimeReportJob.competence.service,
				)} - ${selectedTimeReportJob.customer.name}`,
				url: `/uppdrag/${selectedTimeReportJob.id}`,
			};
	};

	const getMappedShiftList = (shiftList: ScheduleShiftItem[]): ShiftItemWithMissingHours[] => {
		return shiftListWithMissingHours(
			shiftList.sort((a, b) => {
				const startDateA = new Date(a.start).getTime();
				const startDateB = new Date(b.start).getTime();
				return startDateA - startDateB;
			}),
		);
	};

	return (
		<HStack height={"100px"} gap={0}>
			{shiftData.map((item) => (
				<Box key={item.date} borderBottom="1px solid #DEDEDE" height="100px">
					<Box
						minWidth="40px"
						height="50px"
						backgroundColor={moment().isSame(moment(item.date), "day") ? "#DFEAF1" : "#EDEFF3"}
						margin="25px 1px 25px 1px"
						padding="2px"
					>
						{item.shiftItems && item.shiftItems.length > 0 ? (
							<HStack gap={0}>
								{getMappedShiftList(item.shiftItems).map((si, index) => {
									const maxWidth = 36;
									const slotWidth = calculateSlotWidth(si.start, si.end, maxWidth);

									if (si.isValidShift && si.shift?.start && si.shift?.end)
										return (
											<Popover key={index} placement="top-start">
												<PopoverTrigger>
													<Button
														maxWidth={`${slotWidth}px`}
														minWidth={`${slotWidth}px`}
														height="100%"
														p={0}
														backgroundColor={moment().isSame(moment(item.date), "day") ? "#DFEAF1" : "#EDEFF3"}
														borderRadius={"none"}
														gap={0}
													>
														<Box
															minWidth={`${slotWidth}px`}
															maxWidth={`${slotWidth}px`}
															height="46px"
															backgroundColor={getFillColor(si.shift).color}
														/>
													</Button>
												</PopoverTrigger>
												<Portal>
													<PopoverContent borderLeft={`11px solid ${getFillColor(si.shift).color}`}>
														<PopoverCloseButton />
														<PopoverBody>
															{isJobView ? (
																<ShiftToolTip
																	duration={formatTooltipDateRange(si.shift)}
																	description={getFillColor(si.shift).description}
																	consultantData={getConsultantForShift(si.shift) as Consultant}
																/>
															) : (
																<ShiftToolTip
																	duration={formatTooltipDateRange(si.shift)}
																	description={getFillColor(si.shift).description}
																	jobData={getJobDataForConsultant(si.shift)}
																/>
															)}
														</PopoverBody>
													</PopoverContent>
												</Portal>
											</Popover>
										);
									return (
										<Box
											key={index}
											minWidth={`${slotWidth}px`}
											maxWidth={`${slotWidth}px`}
											height="46px"
											backgroundColor={getFillColor(si.shift).color}
										/>
									);
								})}
							</HStack>
						) : (
							<Box minWidth="100%" height="100%" />
						)}
					</Box>
				</Box>
			))}
		</HStack>
	);
};

export default ShiftViewList;
